/** @jsxImportSource preact */
import { supportBlockCard } from "../../conversation/support-requests";
import type {
  CardBlock,
  InboxBlock,
  ReplyState,
  SupportRequestCard,
} from "@go-novus/admin-api";
import {
  emailPayloadSchema,
  supportRequestBlockDataSchema,
} from "@go-novus/admin-api";
import { format, isFuture, parseISO } from "date-fns";
import type {
  CustomerAuth,
  VerifyEmailAction,
} from "../../protocol/customer-auth";
import { verificationPrompt } from "../../conversation/verification";
import * as m from "../../paraglide/messages.js";
import { CustomerVerificationCard } from "../verify/customer-verification-card";
import { SupportRequestReceipt } from "../contact/support-request-receipt";
import { EmailResultCard } from "../contact/email-result-card";
import { REQUEST_TYPE_LABELS } from "../contact/contact-card";
import { requestTypes } from "../contact/request-type";
import { StatusCard } from "../cards/status-card";
import { OrderListCard } from "../order-list/order-list-card";
import { OrderCard as OrderDetail, OrderCardSkeleton } from "./order-card";
import { formatMoney, orderStatusView, paymentLabel } from "./order-display";
import { shippingOrderView } from "./shipping-order-view";
import type { WidgetTokens } from "../theme";

const ORDER_CARD_FEATURES = { notReceivedButton: false };

/**
 * 卡片数据还在取：订单列表、订单结果卡按回复状态先摆同形状的骨架，数据到了原地换成真卡；
 * 其余卡（核验、查单入口、没查到订单、联系商家）共用一张载入中的状态卡。
 */
export function PendingBusinessCards({
  replyStates,
  tokens,
}: {
  replyStates: ReplyState[];
  tokens: WidgetTokens;
}) {
  const others = replyStates.some(
    (state) => state.type !== "order_list" && state.type !== "order_detail",
  );
  return (
    <>
      {replyStates.map((state, index) =>
        state.type === "order_list" ? (
          <OrderListCard key={index} loading orders={[]} tokens={tokens} />
        ) : state.type === "order_detail" ? (
          <OrderCardSkeleton key={index} tokens={tokens} />
        ) : null,
      )}
      {others && (
        <StatusCard
          tokens={tokens}
          icon="search"
          title={m.widget_blocks_loading()}
        />
      )}
    </>
  );
}

export function BusinessOrderCard({
  block,
  readOnly = false,
  supportCard,
  tokens,
  onAction,
  disabled,
  loading,
  verificationBusy,
  playgroundVerification,
  verificationAuth,
  onVerifyEmail,
  onVerify,
  formatDateTime,
  onContactStore,
}: {
  block: CardBlock | InboxBlock;
  readOnly?: boolean;
  supportCard?: SupportRequestCard;
  /** ref 是待验证请求；orderReference 是订单卡上的订单号，预填进采集卡。 */
  onContactStore?: (ref?: string, orderReference?: string) => void;
  tokens: WidgetTokens;
  disabled?: boolean;
  loading?: boolean;
  onAction?: (
    action: "select" | "more" | "not_received" | "check",
    orderRef?: string,
  ) => void;
  verificationBusy?: boolean;
  playgroundVerification?: boolean;
  verificationAuth?: CustomerAuth;
  onVerifyEmail?: VerifyEmailAction;
  onVerify?: (email?: string) => void;
  formatDateTime?: (iso: string) => string;
}) {
  if (block.status === "unavailable")
    return (
      <StatusCard
        tokens={tokens}
        live={!readOnly}
        icon="lock"
        title={m.widget_order_unavailable_title()}
        description={m.widget_order_error_inaccessible()}
        actions={
          block.type === "verification" && onContactStore
            ? [
                {
                  label: m.widget_verify_contact_store(),
                  variant: "ghost",
                  onClick: () => {
                    onContactStore();
                  },
                },
              ]
            : []
        }
      />
    );
  const actions: CardBlock["actions"] = block.actions;
  const loginActions = actions.filter(
    (action): action is "store_login" | "email_code" =>
      action === "store_login" || action === "email_code",
  );
  if (block.status === "needs_verification" || block.type === "verification") {
    if (block.status === "ok" && block.data.status === "verified")
      return readOnly ? (
        <StatusCard
          tokens={tokens}
          icon="lock"
          title={m.widget_order_verified()}
        />
      ) : null;
    const prompt = verificationPrompt(block) ?? {
      requestId: "",
      intent: "continue" as const,
      expiresAt: "9999-12-31T23:59:59Z",
      lifecycle: "active" as const,
    };
    return (
      <CustomerVerificationCard
        prompt={prompt}
        tokens={tokens}
        actions={loginActions}
        busy={verificationBusy || disabled}
        playground={playgroundVerification}
        auth={verificationAuth}
        onVerifyEmail={onVerifyEmail}
        onVerify={onVerify}
        onContactStore={
          onContactStore
            ? () => {
                onContactStore(
                  block.type === "verification" &&
                    block.status === "ok" &&
                    block.data.status === "pending" &&
                    isFuture(parseISO(block.data.expires_at))
                    ? block.data.auth_request_id
                    : undefined,
                );
              }
            : undefined
        }
      />
    );
  }
  if (readOnly && block.type === "support_request") {
    const data = emailPayloadSchema.parse(block.data);
    if ("phase" in data)
      return (
        <StatusCard
          tokens={tokens}
          live={false}
          icon="mail"
          title={
            data.phase === "expired"
              ? m.widget_support_expired_title()
              : m.widget_unsent_title()
          }
          eyebrow={m.widget_support_reference({ id: data.request_id })}
          description={
            data.phase === "expired"
              ? undefined
              : m.widget_unsent_status({
                  type: REQUEST_TYPE_LABELS[requestTypes[data.request_type]](),
                })
          }
        />
      );
    return (
      <EmailResultCard
        tokens={tokens}
        result={{
          requestId: data.request_id,
          requestType: data.request_type,
          contactEmail: data.contact_email,
          to: data.to,
          status: data.send_status,
          acceptedAt: data.accepted_at
            ? formatDateTime?.(data.accepted_at)
            : undefined,
        }}
      />
    );
  }
  if (block.type === "support_request")
    return (
      <SupportRequestReceipt
        card={
          supportCard ??
          supportBlockCard(supportRequestBlockDataSchema.parse(block.data))
        }
        tokens={tokens}
        onOpen={onContactStore}
      />
    );
  if (block.type === "order_check")
    return (
      <StatusCard
        tokens={tokens}
        icon="search"
        title={m.widget_order_check_action()}
        actions={
          onAction
            ? [
                {
                  label: m.widget_order_check_action(),
                  disabled: disabled || loading,
                  onClick: () => {
                    onAction("check");
                  },
                },
              ]
            : []
        }
      />
    );
  if (block.type === "order_list_empty")
    return (
      <StatusCard
        tokens={tokens}
        icon="search"
        live={!readOnly}
        title={m.widget_order_empty_title()}
        description={m.widget_orders_readable_empty()}
      />
    );
  const active = !readOnly && !!onAction && !disabled && !loading;
  if (block.type === "order_list")
    return (
      <OrderListCard
        tokens={tokens}
        readOnly={readOnly}
        orders={block.data.orders.map((order, index) => ({
          id: "order_ref" in order ? order.order_ref : String(index),
          number: order.name,
          imageUrl: order.image_url,
          date: formatDateTime
            ? formatDateTime(order.created_at)
            : format(parseISO(order.created_at), "PP"),
          itemTitle: formatMoney(order.total.amount, order.total.currency),
          moreItems: 0,
        }))}
        disabled={!active}
        hasMore={"next_cursor" in block.data && block.data.next_cursor !== null}
        loadingMore={loading}
        onSelect={
          !readOnly && onAction && actions.includes("select_order")
            ? (ref) => {
                onAction("select", ref);
              }
            : undefined
        }
        onLoadMore={
          !readOnly && onAction && actions.includes("load_more")
            ? () => {
                onAction("more");
              }
            : undefined
        }
      />
    );
  const payload = block.data;
  return (
    <OrderDetail
      tokens={tokens}
      showProgress={false}
      actions={
        ORDER_CARD_FEATURES.notReceivedButton &&
        actions.includes("not_received") &&
        onAction
          ? ["contact", "not_received"]
          : onAction
            ? ["contact"]
            : []
      }
      card={{
        number: payload.name,
        placed: formatDateTime
          ? formatDateTime(payload.created_at)
          : format(parseISO(payload.created_at), "PP"),
        payment: paymentLabel(payload.financial_status),
        status: orderStatusView(payload.order_status),
        total: formatMoney(payload.total.amount, payload.total.currency),
        orderUrl: actions.includes("view_order")
          ? "order_status_url" in payload
            ? payload.order_status_url
            : undefined
          : undefined,
        ...shippingOrderView(
          payload,
          actions.includes("track_shipment"),
          formatDateTime,
        ),
      }}
      onContactStore={
        onContactStore
          ? () => {
              onContactStore(undefined, payload.name);
            }
          : undefined
      }
      onNotReceived={
        active &&
        ORDER_CARD_FEATURES.notReceivedButton &&
        actions.includes("not_received")
          ? () => {
              onAction(
                "not_received",
                "order_ref" in payload ? payload.order_ref : undefined,
              );
            }
          : undefined
      }
    />
  );
}
